import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { TopBar } from "../components/TopBar";
import { api } from "../lib/api";

type ClassRow = {
  id: number;
  name: string;
  invite_code: string;
  org_id: number;
  org_name: string;
  student_count: number;
  created_at: string;
};

export function DashboardPage() {
  const { t, i18n } = useTranslation(["common", "admin"]);
  const { data: classes, isLoading } = useQuery({
    queryKey: ["teacher-classes"],
    queryFn: () => api<ClassRow[]>("/api/teacher/classes"),
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-2xl font-bold text-primary mb-6">{t("admin:dashboard_title")}</h1>

        {isLoading && <p className="text-muted">{t("common:loading")}</p>}

        {!isLoading && (!classes || classes.length === 0) && (
          <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-10 text-center text-muted">
            {t("admin:no_classes")}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {classes?.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Link
                data-testid="class-row"
                to={`/classes/${c.id}`}
                className="block bg-white rounded-2xl shadow-sm hover:shadow-lg transition p-5 border border-gray-100"
              >
                <div className="text-xs text-muted">{c.org_name}</div>
                <div className="text-lg font-bold text-primary mt-1">{c.name}</div>
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm text-body">
                    <span className="text-2xl font-bold text-ops">{c.student_count}</span>
                    <span className="text-xs text-muted ml-1">{i18n.language === "ja" ? "名" : "learners"}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-muted">{t("admin:invite_code")}</div>
                    <div className="text-sm font-mono font-bold text-primary">{c.invite_code}</div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
}
